import { FC } from 'react';
import { Typography, styled } from '@mui/material';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { FocusTheme } from '@/components/Focus/configs/theme';

export type LaunchTypeChipAppearance = 'default' | 'rights-change';
export type LaunchChangeType = 'add' | 'remove';

export type LaunchTypeChipProps = {
  type: 'first-time' | 'incremental';
  appearance?: LaunchTypeChipAppearance;
  changeType?: LaunchChangeType;
};

const getLaunchIcon = ({
  isFirstTimeLaunch,
  isRightsChangeAppearance,
  changeType
}: {
  isFirstTimeLaunch: boolean;
  isRightsChangeAppearance: boolean;
  changeType?: LaunchChangeType;
}) => {
  if (!isRightsChangeAppearance) {
    return isFirstTimeLaunch ? 'star_checked' : 'progress_star';
  }

  if (isFirstTimeLaunch) {
    return 'star_checked';
  }

  return changeType === 'remove' ? 'subtract' : 'add';
};

const LaunchTypeChip: FC<LaunchTypeChipProps> = ({ type, appearance = 'default', changeType }) => {
  const isFirstTimeLaunch = type === 'first-time';
  const isRightsChangeAppearance = appearance === 'rights-change';

  const icon = getLaunchIcon({ isFirstTimeLaunch, isRightsChangeAppearance, changeType });

  return (
    <StyledLaunchType type={type} appearance={appearance} changeType={changeType}>
      <PolarisIcon icon={icon} />
      <StyledLabel>{isFirstTimeLaunch ? 'First Time Launch' : 'Incremental Launch'}</StyledLabel>
    </StyledLaunchType>
  );
};

export default LaunchTypeChip;

const StyledLaunchType = styled('div')<{
  type: 'first-time' | 'incremental';
  appearance: LaunchTypeChipAppearance;
  changeType?: LaunchChangeType;
}>(({ theme, type, appearance, changeType }) => {
  const focusTheme = theme as FocusTheme;

  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 16,
    fontSize: 12,
    fontWeight: 500,
    ...(appearance === 'rights-change' && {
      height: 32
    })
  };

  if (appearance === 'default') {
    return {
      ...baseStyles,
      color: focusTheme.palette.neutral[10],
      border: `1px solid ${focusTheme.palette.neutral[10]}`
    };
  }

  if (type === 'first-time') {
    return {
      ...baseStyles,
      color: '#AA7DFF',
      background: 'rgba(170, 125, 255, 0.2)'
    };
  }

  if (changeType === 'remove') {
    return {
      ...baseStyles,
      color: theme.palette.error.main,
      background: 'rgba(255, 82, 82, 0.2)'
    };
  }

  return {
    ...baseStyles,
    color: theme.palette.primary.main,
    background: 'rgba(118, 170, 241, 0.2)'
  };
});

const StyledLabel = styled(Typography)({
  whiteSpace: 'nowrap',
  fontSize: 'inherit',
  fontWeight: 'inherit'
});
